/* globals mixpanel */

import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {renameProperty} from 'common/report/util/properties';
import {reporter} from 'common/report/error-reporting';

/*
 * Helper function to parse json from successful responses and throw Error
 * if error property is present.
 * @param {Promise} fetchPromise - fetch() request to a JSON endpoint
 * @returns {object} deserialized response if successful
 */
export async function handleJsonRequest(fetchPromise: Promise<Response>) {
  const response = await fetchPromise;
  let body = null;

  try {
    body = await response.json();
  } catch (err) {
    throw new Error(`${response.status}: ${response.statusText}. Please contact support if issue persists.`);
  }

  if (body.error) {
    console.error(new Error(body.error));
    throw new Error(body.error);
  }

  return body;
}

/**
 * Helper function to parse the error to see if it is a data classification error
 * @returns true if the error is a data classification error
 */
export function isDataClassificationError(error: string): boolean {
  // TODO(zihe): Currently this is a workaround to check if it is a data classification error,
  // will need to refactor by introducing errorType to properly define a data classification error
  return !!error && typeof error === `string` && !!/marked classified/i.exec(error);
}

export function trackDataClassificationError(report: string) {
  try {
    mixpanel.track(`[Lexicon] Accessed report with no sensitive data permission`, {'Report name': report});
  } catch (err) {
    reporter.error(err);
  }
}

/**
 * Helper function to parse the error to see if it is a missing datagroup error
 * @returns true if the error is a missing mapped datagroup.
 */
export function isMissingDataGroupError(error: string): boolean {
  return !!error && typeof error === `string` && error === `Missing Datagroup`;
}

export function trackMissingDatagroupError(report: string) {
  try {
    mixpanel.track(`[Lexicon] Mapped property datagroup missing`, {'Report name': report});
  } catch (err) {
    reporter.error(`tracking error`, err);
  }
}

export function getMissingDatagroupMappedProperty(data): string {
  return (
    renameProperty(data.join_property_name) +
    `.` +
    renameProperty(data.datagroup_property_name, {dataGroupId: data.data_group_id})
  );
}

/*
 * Hit legacy Django API endpoints using session auth
 */
export async function djangoFetch(url: string, data = {}, options: any = {}, queryStringParams = {}) {
  const queryString = objToQueryString(queryStringParams);
  const newUrl = url + (queryString ? `?${queryString}` : ``);
  delete options.csrfToken;

  const payload = {
    body: options.method === `GET` ? null : objToQueryString(data),
    credentials: `include`,
    method: options.method || `POST`,
    headers: {
      'Content-Type': `application/x-www-form-urlencoded`,
    },
    ...options,
  };

  if (options.method === `GET`) {
    delete payload.body;
  }

  return fetch(newUrl, payload);
}

/*
 * Wraps djangoFetch with JSON parsing of response and error-handling
 */
export async function djangoFetchJson(url: string, data = {}, options = {}, queryStringParams = {}) {
  return handleJsonRequest(djangoFetch(url, data, options, queryStringParams));
}

/**
 * Wraps common apiClient which wraps http fetch api
 * Handles error handling and response parsing
 * @param url - REST api endpoint url
 * @param fetchParams - Fetch params
 * @param queryParams - a list of optional query params
 */
export async function appApiFetchJson<ResultType = any>(
  url: string,
  fetchParams = {},
  queryParams = {},
): Promise<ResultType> {
  // apiClient's fetch which will automatically fetch working bearer token if expired or missing
  const queryString = objToQueryString(queryParams);
  url += queryString ? `?${queryString}` : ``;
  // TODO: make all appApiFetchJson callers use apiClient.get|post|put|patch|delete instead
  // the helper methods correctly parse errors and unwrap the ResultsType
  // eslint-disable-next-line local-rules/deprecated-member-access
  return (await handleJsonRequest(apiClient.fetch(url, fetchParams))).results;
}
